import {
  CustomerImportAction,
  CustomerImportCommitResult,
  CustomerImportLimits,
  CustomerImportPreviewResult,
  CustomerImportRowResult,
} from "services/customerImportService";

/**
 * Splitting a customer-import CSV that is over the per-upload limits into parts the API
 * takes, and stitching the per-part results back into one, numbered by the original
 * file's lines.
 *
 * Mirrors the server's reader: one record per line (`\r\n`, `\n` or `\r`), the header is
 * line 1, and whitespace-only lines are skipped but still counted. Each part repeats the
 * header; blank lines are dropped from parts.
 *
 * @example
 * const plan = await planImport(file, limits);          // parts.length > 1 → ask before splitting
 * const preview = await previewInParts(plan.parts, (f) => service.preview(f, false), setPart, dupMsg);
 * const { result, committedParts } = await commitInParts(plan.parts, (f) => service.commit(f, false), setPart);
 */

export const DEFAULT_IMPORT_LIMITS: CustomerImportLimits = {
  maxRows: 1000,
  maxFileBytes: 5 * 1024 * 1024,
};

export interface ImportPart {
  file: File;
  /** Original file line of each data row, in order. Absent when the part is the selected file itself. */
  lineNumbers?: number[];
}

export interface ImportPlan {
  rowCount: number;
  parts: ImportPart[];
}

export interface CsvChunk {
  text: string;
  lineNumbers: number[];
}

export interface PartedCommit {
  result: CustomerImportCommitResult;
  /** Parts written before the import stopped; equals the part count when every part was imported. */
  committedParts: number;
  /** Thrown by the part that stopped the import, if it failed rather than rolled back. */
  error?: unknown;
}

const LINE_BREAK = "\r\n";

export const utf8Length = (text: string): number => {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00
    ) {
      bytes += 4;
      i++;
    } else {
      bytes += 3;
    }
  }
  return bytes;
};

export const splitCsvText = (
  text: string,
  limits: CustomerImportLimits,
): { rowCount: number; chunks: CsvChunk[] } => {
  const lines = text.replace(/^﻿/, "").split(/\r\n|\n|\r/);
  const header = lines[0] ?? "";
  const headerBytes = utf8Length(header) + LINE_BREAK.length;
  const chunks: CsvChunk[] = [];
  let rows: string[] = [];
  let lineNumbers: number[] = [];
  let bytes = headerBytes;

  const flush = () => {
    if (rows.length === 0) return;
    chunks.push({
      text: [header, ...rows].join(LINE_BREAK) + LINE_BREAK,
      lineNumbers,
    });
    rows = [];
    lineNumbers = [];
    bytes = headerBytes;
  };

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;
    const lineBytes = utf8Length(line) + LINE_BREAK.length;
    if (
      rows.length >= limits.maxRows ||
      (rows.length > 0 && bytes + lineBytes > limits.maxFileBytes)
    ) {
      flush();
    }
    rows.push(line);
    lineNumbers.push(i + 1);
    bytes += lineBytes;
  }
  flush();

  return {
    rowCount: chunks.reduce((n, chunk) => n + chunk.lineNumbers.length, 0),
    chunks,
  };
};

export const planImport = async (
  file: File,
  limits: CustomerImportLimits,
): Promise<ImportPlan> => {
  const { rowCount, chunks } = splitCsvText(await file.text(), limits);
  if (
    chunks.length === 0 ||
    (chunks.length === 1 && file.size <= limits.maxFileBytes)
  ) {
    return { rowCount, parts: [{ file }] };
  }
  const baseName = file.name.replace(/\.csv$/i, "");
  return {
    rowCount,
    parts: chunks.map((chunk, i) => ({
      file: new File([chunk.text], `${baseName}-part${i + 1}.csv`, {
        type: "text/csv",
      }),
      lineNumbers: chunk.lineNumbers,
    })),
  };
};

const renumber = (
  rows: CustomerImportRowResult[],
  part: ImportPart,
): CustomerImportRowResult[] =>
  part.lineNumbers
    ? rows.map((row) => ({
        ...row,
        rowNumber: part.lineNumbers![row.rowNumber - 2] ?? row.rowNumber,
      }))
    : rows;

const countsOf: Record<CustomerImportAction, "creates" | "updates" | "skips"> =
  {
    Create: "creates",
    Update: "updates",
    Skip: "skips",
  };

export const mergePreviews = (
  parts: ImportPart[],
  results: CustomerImportPreviewResult[],
  duplicateError: string,
): CustomerImportPreviewResult => {
  const merged: CustomerImportPreviewResult = {
    totalRows: 0,
    creates: 0,
    updates: 0,
    skips: 0,
    errors: 0,
    rows: [],
    newCustomerCompanies: [],
  };
  const companies = new Map<string, string>();
  const partOfCustomerId = new Map<string, number>();

  results.forEach((result, partIndex) => {
    merged.totalRows += result.totalRows;
    merged.creates += result.creates;
    merged.updates += result.updates;
    merged.skips += result.skips;
    merged.errors += result.errors;
    result.newCustomerCompanies.forEach((name) => {
      if (!companies.has(name.toLowerCase())) {
        companies.set(name.toLowerCase(), name);
      }
    });

    for (const row of renumber(result.rows, parts[partIndex])) {
      const key = row.customerId.toLowerCase();
      const firstPart = key ? partOfCustomerId.get(key) : undefined;
      if (key && firstPart === undefined) partOfCustomerId.set(key, partIndex);

      if (firstPart === undefined || firstPart === partIndex) {
        merged.rows.push(row);
        continue;
      }
      if (row.errors.length === 0) {
        merged[countsOf[row.action]]--;
        merged.errors++;
      }
      merged.rows.push(
        row.errors.includes(duplicateError)
          ? row
          : { ...row, errors: [...row.errors, duplicateError] },
      );
    }
  });

  merged.newCustomerCompanies = Array.from(companies.values()).sort((a, b) =>
    a.localeCompare(b),
  );
  return merged;
};

export const previewInParts = async (
  parts: ImportPart[],
  previewPart: (file: File) => Promise<CustomerImportPreviewResult>,
  onPart: (partNumber: number) => void,
  duplicateError: string,
): Promise<CustomerImportPreviewResult> => {
  const results: CustomerImportPreviewResult[] = [];
  for (let i = 0; i < parts.length; i++) {
    onPart(i + 1);
    results.push(await previewPart(parts[i].file));
  }
  return mergePreviews(parts, results, duplicateError);
};

export const commitInParts = async (
  parts: ImportPart[],
  commitPart: (file: File) => Promise<CustomerImportCommitResult>,
  onPart: (partNumber: number) => void,
): Promise<PartedCommit> => {
  const result: CustomerImportCommitResult = {
    created: 0,
    updated: 0,
    skipped: 0,
    errored: 0,
    rows: [],
  };

  for (let i = 0; i < parts.length; i++) {
    onPart(i + 1);
    let partResult: CustomerImportCommitResult;
    try {
      partResult = await commitPart(parts[i].file);
    } catch (error) {
      return { result, committedParts: i, error };
    }
    result.rows.push(...renumber(partResult.rows, parts[i]));
    if (partResult.errored > 0) {
      result.errored += partResult.errored;
      return { result, committedParts: i };
    }
    result.created += partResult.created;
    result.updated += partResult.updated;
    result.skipped += partResult.skipped;
  }

  return { result, committedParts: parts.length };
};
